import type { IScope } from "angular";
import { finalize } from "rxjs/operators";

import { remove } from "lodash";

import type { IResponseWithLoading, ISiteSlim, ITeam, ITeamMember } from "App/Modules/Models";
import type { ITeamsRepository } from "App/Modules/Repositories";
import type { IModalService } from "App/Modules/Services";
import { extendWithLoadingAndFail } from "App/Modules/Services/ObservableExtensions";

export default class TeamDetailsComponentController {
    public static $inject: string[] = ["$scope", "$translate", "mmTeamsRepository", "mmModalService"];

    public teamId: string;

    public team: IResponseWithLoading<ITeam>;

    public teamMembers: IResponseWithLoading<ITeamMember[]>;

    public noRoleLabel: string;

    public sitesSubCaption: string;

    public teamMembersSubCaption: string;

    constructor(
        private readonly $scope: IScope,
        private readonly $translate: ng.translate.ITranslateService,
        private readonly teamsRepository: ITeamsRepository,
        private readonly modalService: IModalService,
    ) {}

    public $onInit() {
        if (this.teamId) {
            this.team = { isLoading: true };
            extendWithLoadingAndFail(this.teamsRepository.getTeam(this.teamId))
                .pipe(finalize(() => this.$scope.$applyAsync()))
                .subscribe(
                    (response: IResponseWithLoading<ITeam>) => {
                        if (!response.hasError && response.data) {
                            this.team = response;
                        }
                    },
                    (error) => {
                        this.team = error;
                    },
                );

            this.teamMembers = { isLoading: true };
            extendWithLoadingAndFail(this.teamsRepository.getTeamMembers(this.teamId))
                .pipe(finalize(() => this.$scope.$applyAsync()))
                .subscribe(
                    (response: IResponseWithLoading<ITeamMember[]>) => {
                        if (!response.hasError && response.data) {
                            const teamMembersCount = response.data.length;
                            this.setTeamMembersSubCaption(teamMembersCount);
                            this.teamMembers = response;
                        }
                    },
                    (error) => {
                        this.teamMembers = error;
                    },
                );
        }
    }

    public setLabelsString(teamMember: ITeamMember): string {
        if (teamMember.labels.length > 0) {
            return teamMember.labels.map((l) => l.label).join(", ");
        }
        return this.$translate.instant("Entities.Label.NoLabels");
    }

    public getNoRoleLabel(): string {
        if (!this.noRoleLabel) {
            this.noRoleLabel = this.$translate.instant("Entities.Role.NoRole");
        }
        return this.noRoleLabel;
    }

    public addSite() {
        this.modalService.open<ISiteSlim, ITeam>("mmAddSiteToTeam", null, this.team.data).subscribe(
            (addedSiteResponse) => {
                this.team.data.sites.push(addedSiteResponse);
                const { sitesCount } = this.team.data;
                this.setSitesSubCaption(sitesCount);
            },
            (reason) => reason,
        );
    }

    public deleteSite(site: ISiteSlim) {
        extendWithLoadingAndFail(this.teamsRepository.deleteSite(this.teamId, site.id))
            .pipe(finalize(() => this.$scope.$applyAsync()))
            .subscribe(
                (response: IResponseWithLoading<void>) => {
                    (site as any).actionResponse = response;
                    if (response.status === 200) {
                        remove(this.team.data.sites, (s) => {
                            return s.id === site.id;
                        });
                        const { sitesCount } = this.team.data;
                        this.setSitesSubCaption(sitesCount);
                    }
                },
                (error) => {
                    (site as any).actionResponse = error;
                },
            );
    }

    public addTeamMember() {
        this.modalService.open<ITeamMember, ITeam>("mmAddUserToTeam", null, this.team.data).subscribe(
            (addedUserResponse) => {
                this.teamMembers.data.push(addedUserResponse);
                const teamMembersCount = this.teamMembers.data.length;
                this.setTeamMembersSubCaption(teamMembersCount);
            },
            (reason) => reason,
        );
    }

    public editTeamMember(teamMember: ITeamMember) {
        this.modalService.open<ITeamMember, ITeam>("mmEditTeamMember", teamMember, this.team.data).subscribe(
            (editUserResponse) => {
                if (editUserResponse !== null) {
                    Object.assign(teamMember, editUserResponse);
                } else {
                    remove(this.teamMembers.data, teamMember);
                }

                const teamMembersCount = this.teamMembers.data.length;
                this.setTeamMembersSubCaption(teamMembersCount);
            },
            (reason) => reason,
        );
    }

    private setSitesSubCaption(sitesCount: number) {
        this.$translate("Entities.Site.SitesCountFormat", { sitesCount }, "messageformat").then((translation) => {
            this.sitesSubCaption = translation;
        });
    }

    private setTeamMembersSubCaption(teamMembersCount: number) {
        this.$translate("Entities.TeamMember.NumTeamMembersFormat", { teamMembersCount }, "messageformat").then(
            (translation) => {
                this.teamMembersSubCaption = translation;
            },
        );
    }
}
